// The Inbox: every email to admin@fleetly.co.za as a ticket on a two day
// clock. Xolile owns the clock; the answer can belong to anyone, so a ticket is
// assigned without the clock stopping.
//
// Every fact on this screen comes from Postgres (db/migrations/0331): the order
// of the queue, time left, overdue, paused, who the sender is, the KPI tile.
// The page arranges them. CLAUDE.md rule 4.
//
// REFRESHES EVERY 30 SECONDS while the tab is visible, the same way the
// WhatsApp page does and for the same reason: the tables are granted to nobody
// in the browser, so realtime would mean a second way in. New mail reaches the
// database within a minute of arriving in Gmail.
//
// The chosen ticket is kept in the address (?t=), so a link to a ticket opens
// it. On a narrow screen the queue and the ticket stack.
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, Inbox as InboxIcon, ShieldAlert } from 'lucide-react'
import { useBulkSetStatus, useGetAccess, useGetKpi, useGetOptions, useGetQueue, useGetTicket } from '../hooks/backend/inbox'
import { Button } from '../lib/shadcn/button'
import { Spinner } from '../lib/shadcn/spinner'
import { cn } from '../lib/shadcn/utils'
import { Chip } from '../components/interviews/Chip'
import { KpiTiles } from '../components/inbox/KpiTiles'
import { QueueList, type InboxView } from '../components/inbox/QueueList'
import { TicketPanel } from '../components/inbox/TicketPanel'
import type { Kpi, Options, QueueResult, TicketDetail } from '../components/inbox/types'
import { syncHealth } from '../utils/inbox'

const REFRESH_MS = 30_000

function usePoll(fn: () => void, enabled: boolean) {
  const ref = useRef(fn)
  ref.current = fn
  useEffect(() => {
    if (!enabled) return
    const tick = () => {
      if (document.visibilityState === 'visible') ref.current()
    }
    const id = window.setInterval(tick, REFRESH_MS)
    document.addEventListener('visibilitychange', tick)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', tick)
    }
  }, [enabled])
}

export default function Inbox() {
  const accessQuery = useGetAccess<{ allowed: boolean }>()
  const queueQuery = useGetQueue<QueueResult>()
  const ticketQuery = useGetTicket<TicketDetail>()
  const kpiQuery = useGetKpi<Kpi>()
  const optionsQuery = useGetOptions<Options>()
  const bulk = useBulkSetStatus<{ changed: number }>()

  const [params, setParams] = useSearchParams()
  const selected = params.get('t')
  const [view, setView] = useState<InboxView>('open')
  const [category, setCategory] = useState('')
  const [channel, setChannel] = useState('')
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [ticked, setTicked] = useState<Set<string>>(new Set())
  const [bulkNotice, setBulkNotice] = useState<string | null>(null)

  const loadAccess = accessQuery.trigger
  const loadQueue = queueQuery.trigger
  const loadTicket = ticketQuery.trigger
  const loadKpi = kpiQuery.trigger
  const loadOptions = optionsQuery.trigger

  useEffect(() => {
    void loadAccess({}, { skipCache: true }).catch(() => undefined)
  }, [loadAccess])
  const allowed = accessQuery.data?.allowed === true

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(search.trim()), 300)
    return () => window.clearTimeout(id)
  }, [search])

  const refreshQueue = useCallback(() => {
    void loadQueue(
      { view, category: category || null, channel: channel || null, search: debounced || null },
      { skipCache: true },
    ).catch(() => undefined)
  }, [loadQueue, view, category, channel, debounced])

  const refreshSide = useCallback(() => {
    void loadKpi({}, { skipCache: true }).catch(() => undefined)
    void loadOptions({}, { skipCache: true }).catch(() => undefined)
  }, [loadKpi, loadOptions])

  const refreshTicket = useCallback(() => {
    if (!selected) return
    void loadTicket({ id: selected }, { skipCache: true }).catch(() => undefined)
  }, [loadTicket, selected])

  useEffect(() => {
    if (allowed) refreshQueue()
  }, [allowed, refreshQueue])
  useEffect(() => {
    if (allowed) refreshSide()
  }, [allowed, refreshSide])
  useEffect(() => {
    if (allowed) refreshTicket()
  }, [allowed, refreshTicket])
  useEffect(() => setTicked(new Set()), [view, category, channel, debounced])

  usePoll(() => {
    refreshQueue()
    refreshSide()
    refreshTicket()
  }, allowed)

  const select = (id: string | null) => {
    const next = new URLSearchParams(params)
    if (id) next.set('t', id)
    else next.delete('t')
    setParams(next, { replace: false })
  }

  const onChanged = () => {
    refreshTicket()
    refreshQueue()
    refreshSide()
  }

  const onBulk = async (status: 'resolved' | 'closed' | 'open') => {
    setBulkNotice(null)
    try {
      const res = await bulk.trigger({ ids: [...ticked], status })
      setBulkNotice(`${res?.changed ?? 0} ticket${res?.changed === 1 ? '' : 's'} changed.`)
      setTicked(new Set())
      onChanged()
    } catch (e) {
      setBulkNotice(e instanceof Error ? e.message : String(e))
    }
  }

  if (accessQuery.loading && !accessQuery.data && !accessQuery.error) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  if (accessQuery.error) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md space-y-3 rounded-lg border border-border bg-card p-6 text-center text-sm">
          <p className="text-rose-700 dark:text-rose-300">{accessQuery.error}</p>
          <Button size="sm" variant="outline" onClick={() => void loadAccess({}, { skipCache: true }).catch(() => undefined)}>
            Try again
          </Button>
        </div>
      </div>
    )
  }

  if (!allowed) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">The Inbox is open to the people who answer admin@fleetly.co.za.</p>
          <p className="text-xs text-muted-foreground">
            If you should be one of them, ask an admin to switch on can_use_inbox for you in cockpit access.
          </p>
        </div>
      </div>
    )
  }

  const options = optionsQuery.data ?? null
  const health = syncHealth(options?.sync ?? null)
  const backfilling = options?.sync && !options.sync.backfill_done_at && options.sync.backfill_started_at
  const rows = queueQuery.data?.rows ?? []
  const detail = ticketQuery.data && ticketQuery.data.id === selected ? ticketQuery.data : null

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
          <InboxIcon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-semibold text-foreground">Inbox</h1>
          <p className={cn('text-xs', health.ok ? 'text-muted-foreground' : 'text-rose-700 dark:text-rose-300')}>
            {options?.sync?.mailbox ?? 'admin@fleetly.co.za'}: {health.label}
            {/[.!?]$/.test(health.label) ? '' : '.'}
            {backfilling ? ` Bringing in older mail, ${options?.sync?.backfill_threads ?? 0} threads read so far.` : ''}
          </p>
        </div>
        {kpiQuery.data && kpiQuery.data.open_overdue > 0 && (
          <Chip tone="brand" icon={<AlertTriangle className="h-3 w-3" />}>
            {kpiQuery.data.open_overdue} overdue
          </Chip>
        )}
      </div>

      {/* On a phone with a ticket open, the thread needs the height more than
          the tiles do. They come back with the Back button. */}
      <div className={cn(selected ? 'hidden md:block' : 'block')}>
        <KpiTiles kpi={kpiQuery.data ?? null} error={kpiQuery.error ?? null} />
      </div>

      {queueQuery.error && (
        <div className="border-y border-border bg-rose-50 px-4 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">
          {queueQuery.error}
        </div>
      )}

      {(ticked.size > 0 || bulkNotice) && (
        <div className="flex flex-wrap items-center gap-2 border-y border-border bg-muted/50 px-4 py-2 text-sm">
          {ticked.size > 0 && (
            <>
              <span className="font-medium">{ticked.size} selected</span>
              <Button size="sm" variant="outline" className="h-7" disabled={bulk.loading} onClick={() => void onBulk('resolved')}>
                Resolve
              </Button>
              <Button size="sm" variant="outline" className="h-7" disabled={bulk.loading} onClick={() => void onBulk('closed')}>
                Close
              </Button>
              <Button size="sm" variant="ghost" className="h-7" disabled={bulk.loading} onClick={() => void onBulk('open')}>
                Reopen
              </Button>
              <Button size="sm" variant="ghost" className="h-7" onClick={() => setTicked(new Set(rows.map((r) => r.id)))}>
                Select all {rows.length} shown
              </Button>
              <Button size="sm" variant="ghost" className="h-7" onClick={() => setTicked(new Set())}>
                Clear
              </Button>
            </>
          )}
          {bulkNotice && <span className="text-xs text-muted-foreground">{bulkNotice}</span>}
        </div>
      )}

      <div className="flex min-h-0 flex-1 border-t border-border">
        <aside className={cn('min-h-0 w-full border-r border-border md:w-96 md:shrink-0', selected ? 'hidden md:block' : 'block')}>
          <QueueList
            rows={rows}
            total={queueQuery.data?.total ?? 0}
            loading={queueQuery.loading}
            loaded={queueQuery.data !== null && queueQuery.data !== undefined}
            view={view}
            onView={setView}
            category={category}
            onCategory={setCategory}
            channel={channel}
            onChannel={setChannel}
            search={search}
            onSearch={setSearch}
            options={options}
            selected={selected}
            onSelect={(id) => select(id)}
            ticked={ticked}
            onTick={(id, on) =>
              setTicked((prev) => {
                const next = new Set(prev)
                if (on) next.add(id)
                else next.delete(id)
                return next
              })
            }
          />
        </aside>
        <section className={cn('min-h-0 min-w-0 flex-1 flex-col', selected ? 'flex' : 'hidden md:flex')}>
          <TicketPanel
            detail={detail}
            loading={ticketQuery.loading}
            error={selected ? ticketQuery.error ?? null : null}
            options={options}
            onBack={() => select(null)}
            onChanged={onChanged}
          />
        </section>
      </div>
    </div>
  )
}
